import { useEffect, useMemo, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { LoginService } from '@ey/mfe-ui-core';
import { EventMessage, EventType } from '@azure/msal-browser';
import { useAuth0 } from '@auth0/auth0-react';
import { LoggerService } from '@ey/mfe-ui-core';
import { useMsal } from '@azure/msal-react';
import { Auth0TokenProvider } from './config/Auth';
// import Cookies from 'js-cookie';

import './main.scss';

const { REACT_APP_HOME_URL, REACT_APP_APPINSIGHTS_INSTRUMENTATION_KEY, REACT_APP_LOG_LOGIN_EVENT, REACT_APP_DCRM_URL } =
  process.env;

const loginService = new LoginService();

export const App = () => {
  const { instance, accounts } = useMsal();
  const { getAccessTokenSilently, loginWithPopup } = useAuth0();

  const connectionString = REACT_APP_APPINSIGHTS_INSTRUMENTATION_KEY!;
  const loggerService = useMemo(() => new LoggerService(connectionString), [connectionString]);

  const urlParams = new URLSearchParams(window.location.search);
  const return_to = urlParams.get('return_to');

  const getDCRMToken = useCallback(async () => {
    let dcrmToken = { accessToken: '', expiresOn: Date.now() };

    if (accounts && accounts[0]) {
      const request = {
        scopes: [`${REACT_APP_DCRM_URL}/user_impersonation`],
        account: accounts[0] // Assuming the active account is the first account in the list
      };
      const authResult = await instance.acquireTokenSilent(request);
      dcrmToken = { accessToken: authResult.accessToken, expiresOn: authResult.expiresOn?.getTime() || 0 };
      sessionStorage.setItem('dcrmToken', JSON.stringify(dcrmToken));
    }
  }, [instance, accounts]);

  useEffect(() => {
    loginService.attemptSSOSilent('loginPopup');
    getDCRMToken();
  }, [getDCRMToken]);

  useEffect(() => {
    // MSAL Event Callbacks if required
    const callbackId = instance.addEventCallback((message: EventMessage) => {
      switch (message.eventType) {
        case EventType.LOGIN_START:
        case EventType.SSO_SILENT_START:
          console.log('loading');
          break;
        case EventType.LOGIN_SUCCESS:
        case EventType.SSO_SILENT_SUCCESS: {
          sessionStorage.setItem('accessToken', message.payload!['accessToken']);
          sessionStorage.setItem('expTokenAt', message.payload!['expiresOn'].getTime());
          const token = message.payload!['accessToken'];
          if (token !== '') {
            // Get the user's profile from Graph
            loginService
              .getUserInformation(token)
              .then((userDetail) => {
                sessionStorage.setItem('userDetail', JSON.stringify(userDetail));
              })
              .catch((error) => {
                console.error(error);
              });
          }
          break;
        }
        case EventType.LOGOUT_SUCCESS:
          sessionStorage.removeItem('accessToken');
          sessionStorage.removeItem('userDetail');
          sessionStorage.removeItem('dcrmToken');
          break;
        default:
          break;
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const auth0TokenProvider = Auth0TokenProvider();
    const authenticateUserAuth0 = async (userEmail) => {
      try {
        await getAccessTokenSilently();

        const idToken = await auth0TokenProvider.getAuth0IdTokenClaims();
        if (idToken) {
          sessionStorage.setItem('idToken', idToken);
        }
      } catch (err: any) {
        if (err.error === 'login_required') {
          console.log('ERROR TYPE', err.error);
          if (userEmail) {
            await loginWithPopup({
              authorizationParams: {
                login_hint: userEmail
              }
            });
          } else {
            await loginWithPopup();
          }
        }
      }
    };

    // Get the cookie value
    const getCookie = (name) => {
      const dc = document.cookie;
      const prefix = name + '=';
      let begin = dc.indexOf('; ' + prefix);
      let end;
      if (begin === -1) {
        begin = dc.indexOf(prefix);
        if (begin !== 0) return null;
      } else {
        begin += 2;
        end = document.cookie.indexOf(';', begin);
        if (end === -1) {
          end = dc.length;
        }
      }
      return decodeURI(dc.substring(begin + prefix.length, end));
    };

    const callbackId = instance.addEventCallback(async (message: EventMessage) => {
      if (message.eventType === EventType.LOGIN_SUCCESS || message.eventType === EventType.SSO_SILENT_SUCCESS) {
        if (message.payload) {
          // Check if the cookie is already exist
          const username = getCookie('username');
          const email = getCookie('email');

          const userDetail = message.payload['account'];

          if (userDetail && username === null && email === null && REACT_APP_LOG_LOGIN_EVENT === 'true') {
            // Set the cookie
            document.cookie = 'email=' + message.payload['account'].username + '; path=/; max-age=86400;'; // Expires in 1 day
            document.cookie = 'username=' + message.payload['account'].name + '; path=/; max-age=86400;'; // Expires in 1 day

            const userData = {
              appId: '1',
              eventId: '1',
              message: `${message.payload['account'].name || ''} logged in successfully`,
              status: 'Logged In successfully',
              returnTo: return_to,
              userName: message.payload['account'].name || '',
              email: message.payload['account'].username || ''
            };

            // log the login event
            loggerService.logCustomEvent(`${message.payload['account'].name} logged in...`, userData);
          }

          await authenticateUserAuth0(message.payload['account'].username);

          setTimeout(() => {
            if (return_to !== null) {
              window.open(`${REACT_APP_HOME_URL}/${return_to}`, '_self');
            } else {
              window.open(`${REACT_APP_HOME_URL}`, '_self');
            }
          }, 5000);
        }
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Outlet />;
};
